import { Dialog } from '@headlessui/react'
import React, { ReactNode, useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faRocket, faScrewdriverWrench } from '@fortawesome/pro-light-svg-icons'
import { faDroplet } from '@fortawesome/pro-light-svg-icons/faDroplet'
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons/faQuestionCircle'
import { faCube } from '@fortawesome/pro-regular-svg-icons'
import { IconDefinition, faBars, faBookOpen, faChartLine, faHouse, faTimes } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'

import {
  DEVELOPER_DASHBOARD_URL,
  METAMASK_DOCS_DEVELOPER_URL,
  METAMASK_DOCS_FAUCET_URL,
  METAMASK_SUPPORT_URL
} from '@infura/packages.common/constants'
import { FEATURE_FLAGS } from '@infura/packages.common/constants'

import FeatureToggle from 'components/FeatureToggle'
import { MetaMaskDeveloperFox, MetaMaskLogoVertical } from 'components/MetaMaskLogo'
import { ExternalNavLink, NavLink, Web3AuthLink } from 'components/navigation/NavLink'
import { SettingsLinks, SettingsMenu } from 'components/navigation/SettingsMenu'
import { StatusLink } from 'components/navigation/StatusLink'
import { UpgradeButton } from 'components/navigation/UpgradeButton'

import { useIpfsNetworkEnabled } from 'hooks/ipfs'

const initClickEventProps = {
  location: ' navbar',
  type: 'Text'
}

const NavSection = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <div className={clsx('px-5', className)}>
      <div className='space-y-1'>{children}</div>
    </div>
  )
}

const faInfuraNode: IconDefinition = {
  // Any name you like
  icon: [
    16, // width
    14, // height
    [], // ligatures
    '', // unicode (if relevant)
    'M0.46624 0.213623V2.27922L5.40032 1.77074H7.22144V5.1141L4.15488 6.05202L0 7.05394L0.61696 9.07346L4.5984 7.54322L7.22144 6.74162V12.2296H5.40032L0.46624 11.7208V13.7864H15.5338V11.7208L10.5997 12.2296H8.77856V6.74162L11.3885 7.53906L15.3834 9.07346L16 7.05394L11.8563 6.0549L8.77856 5.1141V1.77074H10.5997L15.5338 2.27922V0.213623H0.46624Z' // svg path data
  ],

  // @ts-expect-error - types don't include iconName
  iconName: 'iconInfuraNode',
  // Use a prefix like 'fac' that doesn't conflict with a prefix in the standard Font Awesome styles
  // (So avoid fab, fal, fas, far, fa)
  // @ts-expect-error - types don't include prefix
  prefix: 'icon'
}

const PrimaryLinks = () => {
  return (
    <NavSection>
      <NavLink
        customPathMatcher={path => path === '/'}
        data-testid='keys-link'
        href='/'
        icon={faHouse}
        label='Home'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Home',
          clickUrl: `${DEVELOPER_DASHBOARD_URL}/`
        }}
      />
      <NavLink
        data-testid='stats-link'
        href='/dashboard/stats'
        icon={faChartLine}
        label='Stats'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Stats',
          clickUrl: `${DEVELOPER_DASHBOARD_URL}/stats`
        }}
      />
    </NavSection>
  )
}
const Products = () => {
  const ipfsEnabled = useIpfsNetworkEnabled()

  return (
    <NavSection>
      <h3 className='text-text-muted mb-2 px-4 text-xs font-medium'>Products</h3>
      <NavLink
        customActiveClass='text-[#FF5833]'
        customPathMatcher={path => path?.startsWith('/key/active-endpoints') || path?.startsWith('/key/all-endpoints')}
        data-testid='keys-link'
        href='/key/all-endpoints'
        icon={faInfuraNode}
        key='infura-rpc'
        label='Infura RPC'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Infura RPC',
          clickUrl: `${DEVELOPER_DASHBOARD_URL}/key`
        }}
      />
      <NavLink
        data-testid='mm-sdk-link'
        href='/mm-sdk'
        icon={faScrewdriverWrench}
        label='MetaMask SDK'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'MetaMask SDK',
          clickUrl: `${DEVELOPER_DASHBOARD_URL}/mm-sdk`
        }}
      />

      <NavLink
        data-testid='developer-api-link'
        href='/developer-api'
        icon={faRocket}
        label='Developer APIs'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Developer APIs',
          clickUrl: `${DEVELOPER_DASHBOARD_URL}/developer-api`
        }}
      />

      <FeatureToggle flag={FEATURE_FLAGS.WEB_3_AUTH_NAV_LINK}>
        <Web3AuthLink />
      </FeatureToggle>

      {ipfsEnabled ? (
        <NavLink
          dataTestid='ipfs-link'
          href='/dashboard/explorer'
          icon={faCube}
          key='ipfs-link'
          label='IPFS'
          trackClickEvent={{
            ...initClickEventProps,
            clickText: 'IPFS',
            clickUrl: `${DEVELOPER_DASHBOARD_URL}/dashboard/explorer`
          }}
        />
      ) : null}
    </NavSection>
  )
}

const ExternalLinks = () => {
  return (
    <>
      <StatusLink />
      <ExternalNavLink
        data-testid='docs-link'
        href={METAMASK_DOCS_DEVELOPER_URL}
        icon={faBookOpen}
        label='Docs'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Docs',
          clickUrl: METAMASK_DOCS_DEVELOPER_URL
        }}
      />
      <ExternalNavLink
        data-testid='faucet-link'
        href={METAMASK_DOCS_FAUCET_URL}
        icon={faDroplet}
        label='Faucet'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Faucet',
          clickUrl: METAMASK_DOCS_FAUCET_URL
        }}
      />
      <ExternalNavLink
        data-testid='support-link'
        href={METAMASK_SUPPORT_URL}
        icon={faQuestionCircle}
        label='Help'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Help',
          clickUrl: METAMASK_SUPPORT_URL
        }}
      />
    </>
  )
}

const VerticalNavbar = () => {
  return (
    <nav className='bg-background-default fixed left-0 top-0 hidden h-screen min-w-[230px] max-w-[230px] flex-col overflow-visible md:flex'>
      {/* Fixed header */}
      <div className='mb-5 px-9 pt-8'>
        <MetaMaskLogoVertical />
      </div>

      {/* Scrollable middle section */}
      <div className='flex flex-1 flex-col gap-6 overflow-auto py-5'>
        <PrimaryLinks />

        <Products />

        <NavSection className='mt-auto overflow-visible'>
          <ExternalLinks />
          <SettingsMenu className='relative' />
        </NavSection>
      </div>

      {/* Fixed footer */}
      <div className='mt-auto p-4'>
        <UpgradeButton />
      </div>
    </nav>
  )
}

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false)

  const closeMenu = () => setIsOpen(false)
  const openMenu = () => setIsOpen(true)

  return (
    <>
      {/* Menu Button */}
      <div className='bg-background-default flex justify-between px-6 py-4 md:hidden'>
        <MetaMaskDeveloperFox />
        <button onClick={openMenu}>
          <FontAwesomeIcon className='text-text-default h-6 w-6' icon={faBars} />
        </button>
      </div>

      {/* Menu Dialog */}
      <Dialog as='div' className='fixed inset-0 z-50 md:hidden' onClose={closeMenu} open={isOpen}>
        <Dialog.Overlay className='fixed inset-0 bg-black/20' />

        <div
          // This catches all clicks that bubble up from the children, so that menu will close on navigation.
          className='bg-background-default text-text-default fixed inset-y-0 left-0 flex w-full flex-col'
          onClick={e => {
            // @ts-expect-error - types don't include closest doesn't know about closest
            const isClickableElement = e.target.closest('a, button')
            if (isClickableElement) {
              closeMenu()
            }
          }}
        >
          {/* Header */}
          <div className='bg-background-default flex justify-between px-6 py-4'>
            <MetaMaskDeveloperFox />
            <button onClick={closeMenu}>
              <FontAwesomeIcon className='text-text-default h-6 w-6' icon={faTimes} />
            </button>
          </div>

          <div className='flex flex-1 flex-col gap-6 overflow-auto py-5'>
            <PrimaryLinks />

            <Products />

            <NavSection className='overflow-visible'>
              <ExternalLinks />
            </NavSection>

            <NavSection className='mt-auto overflow-visible'>
              <SettingsLinks />
            </NavSection>
          </div>

          <div className='mt-auto p-4'>
            <UpgradeButton />
          </div>
        </div>
      </Dialog>
    </>
  )
}

export const Navbar = () => {
  return (
    <>
      <VerticalNavbar />
      <MobileNav />
    </>
  )
}
